import { Close } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Drawer, IconButton, Modal, Paper, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Fragment, ReactNode, useState } from "react";
import { GobikesApi } from "../../config/Apis";
import { getAuthCookie } from "../auth/AuthUtil";
import { OrderModel } from "../utils/Models";

interface FormState {
  loading?: boolean;
  error?: boolean;
  message?: string;
}

export function DamageInvoice(props: {
  children: ReactNode;
  order: OrderModel;
  style?: any;
  backFn: () => void;
}) {
  const [open, setOpen]           = useState(false);
  const [amount, setAmount]       = useState<number>(0);
  const [remark, setRemark]       = useState<string>('');
  const [formState, setFormState] = useState<FormState>({});

  const authCookie = getAuthCookie();
  const theme = useTheme();
  const isPhone = !useMediaQuery(theme.breakpoints.up("md"));

  function handleOpen() {
    setAmount(0);
    setRemark('');
    setFormState({});
    setOpen(true);
  }

  function handleClose() { setOpen(false); }

  async function handleSubmit(e: any) {
    e.preventDefault();

    if (!amount || amount <= 0) {
      setFormState({ error: true, message: 'Enter a valid amount' });
      return;
    }
    if (!remark || !remark.trim()) {
      setFormState({ error: true, message: 'Enter a remark' });
      return;
    }

    setFormState({ loading: true });
    try {
      await GobikesApi.post(
        `/vendors/hubs/${props.order.hubId}/orders/${props.order.id}/damage-invoice`,
        { amount, remark: remark.trim() },
        { headers: { 'vendor-token': authCookie } }
      );
      setFormState({ error: false, message: 'Damage invoice recorded!' });
      setTimeout(() => { handleClose(); props.backFn(); }, 2000);
    } catch (e: any) {
      const errorMessage = (e.response?.data?.message) ? 'Error: ' + e.response.data.message : 'An error occurred';
      setFormState({ error: true, message: errorMessage, loading: false });
    }
  }

  const amountField = (
    <TextField
      label="Amount (₹)"
      type="number"
      size="small"
      fullWidth
      value={amount || ''}
      onChange={(e) => setAmount(Number(e.target.value))}
      sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '14px' }, '& label': { fontSize: '13px' } }}
    />
  );

  const remarkField = (
    <TextField
      label="Remark"
      size="small"
      fullWidth
      value={remark}
      onChange={(e) => setRemark(e.target.value)}
      sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '14px' }, '& label': { fontSize: '13px' } }}
    />
  );

  const message = formState.message && (
    <Typography sx={{ fontSize: '12px', color: formState.error ? '#c0392b' : '#27ae60', mt: 0.5, mb: 0.5 }}>
      {formState.message}
    </Typography>
  );

  return (
    <Fragment>
      <span onClick={handleOpen} style={props.style}>
        {props.children}
      </span>

      {isPhone ? (
        // Phone — bottom drawer, matching the "Record Cash Payment" bottom sheet
        <Drawer
          anchor="bottom"
          open={open}
          onClose={handleClose}
          PaperProps={{ sx: { borderTopLeftRadius: 18, borderTopRightRadius: 18, padding: '20px 20px 40px' } }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, fontSize: '16px' }}>Damage Invoice</Typography>
          {amountField}
          {remarkField}
          {message}
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <LoadingButton
              variant="contained"
              loading={Boolean(formState.loading)}
              sx={{ flex: 1, borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
              onClick={handleSubmit}
            >
              Record
            </LoadingButton>
            <Button
              variant="outlined"
              sx={{ borderRadius: '10px', textTransform: 'none' }}
              onClick={handleClose}
            >
              Cancel
            </Button>
          </Box>
        </Drawer>
      ) : (
        // Desktop — centered modal
        <Modal open={open} className="gbk-modal" onClose={handleClose}>
          <Box className="gbk-modal-content" style={{ width: 'min(380px, calc(100vw - 32px))', height: 'auto', maxHeight: 'calc(100vh - 80px)', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', position: 'absolute', overflowY: 'auto' }}>
            <Paper className="gbk-paper" sx={{ borderRadius: '16px !important', overflow: 'hidden', height: 'auto !important' }}>

              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 700 }}>Damage Invoice</Typography>
                <IconButton onClick={handleClose} size="small" sx={{ color: '#666' }}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>

              {/* Form */}
              <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                {amountField}
                {remarkField}
                {message}
              </Box>

              {/* Footer */}
              <Box sx={{ px: 2.5, pb: 2.5 }}>
                <form onSubmit={handleSubmit}>
                  <LoadingButton
                    variant="contained"
                    type="submit"
                    fullWidth
                    loading={formState.loading}
                    sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, fontSize: '14px', py: 1.2, background: '#8dbe2e', '&:hover': { background: '#78a225' } }}
                  >
                    Record
                  </LoadingButton>
                </form>
              </Box>

            </Paper>
          </Box>
        </Modal>
      )}
    </Fragment>
  );
}
